import { Drawer } from '@material-ui/core';
import { FC, useMemo, useState } from 'react';

import { DisposalRecordFloorUnit } from '@agency/apis/disposals';
import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { RequirementShowItem } from '@shared/apis/requirementsApi.types';
import { useValidateDisposalLettingPublishQuery } from '@shared/apis/transactionsLettingsApi';
import { AtlasValidationErrorResponse, LettingTransactionsShowItem } from '@shared/apis/transactionsLettingsApi.types';
import { useValidateDisposalSalePublishQuery } from '@shared/apis/transactionsSalesApi';
import { AtlasPublishOptionsPanel } from '@shared/components/atlas-transaction-form/AtlasPublishOptionsPanel';
import { Button } from '@shared/components/button';
import { Button as NewButton } from '@shared/components/button-new';
import { CreateNewDrawerFooter } from '@shared/components/create-new-drawer-footer';
import { DrawerHeader } from '@shared/components/drawer-header';
import { validateSpaces } from '@shared/components/record-spaces-taken-form';
import { CreateTransactionDrawer } from '@shared/drawers/create-transaction-drawer';
import { CreateTransactionTypes } from '@shared/drawers/create-transaction-drawer/types';
import { RequirementRecordSpaceCard } from '@shared/drawers/requirement-record-drawer/components/requirement-record-space-card';
import { PageViewState, usePageState } from '@shared/hooks/viewState';
import { TransactionType } from '@shared/models/transactions/transactions';
import { NewButtonColour } from '@shared/types/common/button';
import { getLettingsAtlasPublishFields } from '@shared/utils/atlas-publish-fields';

import { styles } from './RequirementTransactionDrawer.styles';

export interface RequirementTransactionDrawerProps extends WithStyles<typeof styles> {
  requirement: RequirementShowItem;
  transactionType?: TransactionType;
  onClose: () => void;
}

type TenureSelection = 'lease' | 'sale' | null;

interface SpaceFormData {
  buildingTypes: string[];
  entireProperty: StringBoolean;
  floorUnits: DisposalRecordFloorUnit[];
  onShortlist: string;
  matchIdSelected: string;
  sizeMetric: Metric;
  sizeValue: string;
}

interface ValidationErrors {
  addressError: boolean;
  buildingTypesError: boolean;
  error: string;
  onShortlistError: boolean;
  errorDisposal: boolean;
  sizeError: boolean;
  atlasValidationErrors: AtlasValidationErrorResponse | null;
  spaceCardErrors: Record<number, import('@shared/components/record-spaces-taken-form').RecordSpaceItemError>;
}

interface ApiErrorResponse {
  response?: {
    data?: {
      message?: string;
      errors?: AtlasValidationErrorResponse;
    };
    status?: number;
  };
}

const getInitialPageState = () => {
  return {
    publish_options: '',
  } satisfies PageViewState;
};

const RequirementTransactionDrawerComponent: FC<RequirementTransactionDrawerProps> = ({
  classes,
  requirement,
  onClose,
}) => {
  const { pageState, updatePageState } = usePageState(() => getInitialPageState());

  const [tenureSelection, setTenureSelection] = useState<TenureSelection>(null);
  const [spaceFormData, setSpaceFormData] = useState<SpaceFormData>({
    buildingTypes: [],
    entireProperty: '1',
    floorUnits: [],
    onShortlist: '',
    matchIdSelected: '',
    sizeMetric: requirement.size_metric || 'sqft',
    sizeValue: '',
  });
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({
    addressError: false,
    buildingTypesError: false,
    error: '',
    onShortlistError: false,
    errorDisposal: false,
    sizeError: false,
    atlasValidationErrors: null,
    spaceCardErrors: {} as Record<number, import('@shared/components/record-spaces-taken-form').RecordSpaceItemError>,
  });
  const [fieldVisibility, setFieldVisibility] = useState<Record<string, boolean>>({});

  const requiresBothTenureOptions = requirement.to_let && requirement.for_sale;

  const isLettingTransaction = useMemo(() => {
    if (requiresBothTenureOptions) {
      return tenureSelection === 'lease';
    }
    return requirement.to_let;
  }, [requiresBothTenureOptions, tenureSelection, requirement.to_let]);

  const validateLettingPublishParams = useMemo(() => {
    return {
      request_source: 'disposal' as const,
      signature_date: '',
      building_types: [],
      disposal_id: null,
      match_id: spaceFormData.matchIdSelected || undefined,
      property_id: undefined,
    };
  }, [spaceFormData.matchIdSelected]);

  const validateSalePublishParams = useMemo(() => {
    return {
      request_source: 'disposal' as const,
      completion_date: '',
      building_types: [],
      our_disposal: false,
      our_acquisition: false,
      disposal_id: null,
      match_id: spaceFormData.matchIdSelected || undefined,
      property_id: undefined,
      address: undefined,
    };
  }, [spaceFormData.matchIdSelected]);

  const validateLettingPublishQuery = useValidateDisposalLettingPublishQuery(validateLettingPublishParams, {
    enabled: false,
  });

  const validateSalePublishQuery = useValidateDisposalSalePublishQuery(validateSalePublishParams, {
    enabled: false,
  });

  const handleTenureSelection = (tenure: TenureSelection) => {
    setTenureSelection(tenure);
  };

  const handleSpaceFormDataUpdate = (data: Partial<SpaceFormData>) => {
    setSpaceFormData((prev) => ({ ...prev, ...data }));
  };

  const handleBack = () => {
    if (requiresBothTenureOptions) {
      setTenureSelection(null);
    }
  };

  const handleClose = () => {
    setTenureSelection(null);
    setSpaceFormData({
      buildingTypes: [],
      entireProperty: '1',
      floorUnits: [],
      onShortlist: '',
      matchIdSelected: '',
      sizeMetric: requirement.size_metric || 'sqft',
      sizeValue: '',
    });
    setValidationErrors({
      addressError: false,
      buildingTypesError: false,
      error: '',
      onShortlistError: false,
      errorDisposal: false,
      sizeError: false,
      atlasValidationErrors: null,
      spaceCardErrors: {},
    });
    updatePageState({ publish_options: '' });
    onClose();
  };

  const handleProceedToAtlasPublishing = async () => {
    const errors: ValidationErrors = {
      addressError: false,
      buildingTypesError: false,
      error: '',
      onShortlistError: false,
      errorDisposal: false,
      sizeError: false,
      atlasValidationErrors: null,
      spaceCardErrors: {} as Record<number, import('@shared/components/record-spaces-taken-form').RecordSpaceItemError>,
    };

    if (!spaceFormData.onShortlist) {
      errors.onShortlistError = true;
      errors.error = t('please_select_an_option');
      setValidationErrors(errors);
      return;
    }

    if (spaceFormData.onShortlist === '0' && !spaceFormData.matchIdSelected) {
      errors.errorDisposal = true;
      errors.error = t('please_select_a_property');
      setValidationErrors(errors);
      return;
    }

    const spaceValidationResult = validateSpaces(spaceFormData.floorUnits);
    if (spaceValidationResult.hasErrors) {
      setValidationErrors((prev) => ({
        ...prev,
        spaceCardErrors: spaceValidationResult.errors,
        error: t('please_fix_the_errors_above_and_try_again'),
      }));
      return;
    }

    if (!spaceFormData.sizeValue) {
      setValidationErrors((prev) => ({
        ...prev,
        sizeError: true,
        error: t('please_fix_the_errors_above_and_try_again'),
      }));
      return;
    }

    try {
      const validateQuery = isLettingTransaction ? validateLettingPublishQuery : validateSalePublishQuery;
      const validationResult = await validateQuery.refetch();

      if (validationResult.isError && validationResult.error) {
        const apiError = validationResult.error as ApiErrorResponse;
        const errorResponseData = apiError.response?.data;
        const statusCode = apiError.response?.status;

        if (statusCode === 500) {
          const serverErrorMessage = t(
            'an_unexpected_error_occurred_on_the_server_our_team_has_been_notified_please_try_again_later_if_the_problem_persists_contact_support'
          );
          setValidationErrors((prev) => ({
            ...prev,
            error: serverErrorMessage,
            atlasValidationErrors: null,
          }));
          return;
        }

        if (errorResponseData) {
          let errorMessage = t('please_fix_validation_errors_before_publishing_to_atlas');

          if (errorResponseData.message) {
            errorMessage = errorResponseData.message;
          } else if (errorResponseData.errors?.mandatory_errors?.length) {
            errorMessage =
              errorResponseData.errors.mandatory_errors[0]?.message ||
              t('please_fix_validation_errors_before_publishing_to_atlas');
          }

          const errors = errorResponseData.errors;

          setValidationErrors((prev) => ({
            ...prev,
            error: errorMessage,
            atlasValidationErrors: errors ? (errors as unknown as AtlasValidationErrorResponse) : null,
          }));
          return;
        }
      }

      updatePageState({ publish_options: 'true' });
    } catch (err) {
      setValidationErrors((prev) => ({
        ...prev,
        error: t('failed_to_validate_transaction_please_try_again'),
        atlasValidationErrors: null,
      }));
    }
  };

  const renderContent = () => {
    if (requiresBothTenureOptions && tenureSelection === null) {
      return (
        <div className={classes.content}>
          <h3 className={classes.tenureHeading}>{t('what_type_of_transaction_is_this')}</h3>
          <div className={classes.tenureOptions}>
            <Button text={t('lease')} onClick={() => handleTenureSelection('lease')} />
            <Button text={t('sale')} onClick={() => handleTenureSelection('sale')} />
          </div>
        </div>
      );
    }

    return (
      <div className={classes.content}>
        <RequirementRecordSpaceCard
          data={spaceFormData}
          errors={{
            addressError: validationErrors.addressError,
            buildingTypesError: validationErrors.buildingTypesError,
            errorDisposal: validationErrors.errorDisposal,
            onShortlistError: validationErrors.onShortlistError,
            sizeError: validationErrors.sizeError,
            spaceCardErrors: validationErrors.spaceCardErrors,
          }}
          requirementId={requirement.id.toString()}
          investment={requirement.is_investment_sale}
          onUpdateData={handleSpaceFormDataUpdate}
        />
      </div>
    );
  };

  const getFooterButtons = () => {
    if (requiresBothTenureOptions && tenureSelection === null) {
      return null;
    }

    const canProceed =
      spaceFormData.onShortlist &&
      (spaceFormData.onShortlist === '1' || spaceFormData.matchIdSelected) &&
      spaceFormData.sizeValue;

    return (
      <CreateNewDrawerFooter
        text={requiresBothTenureOptions ? t('back') : t('cancel')}
        onBackClick={requiresBothTenureOptions ? handleBack : handleClose}
        validationErrorText={validationErrors.error}
        button={
          <NewButton
            text={t('save_and_continue_to_atlas_publishing')}
            colour={NewButtonColour.PrimaryNavy}
            onClick={handleProceedToAtlasPublishing}
            disabled={!canProceed}
          />
        }
      />
    );
  };

  const getTransactionConfig = () => {
    if (requiresBothTenureOptions) {
      if (tenureSelection === 'lease') {
        return { type: TransactionType.lettings, createType: CreateTransactionTypes.LEASE };
      }
      if (tenureSelection === 'sale') {
        const createType = requirement.is_investment_sale
          ? CreateTransactionTypes.INVESTMENT_SALE
          : CreateTransactionTypes.OCCUPATIONAL_SALE;
        return { type: TransactionType.sales, createType };
      }
      return { type: TransactionType.lettings, createType: null };
    }

    if (requirement.to_let) {
      return { type: TransactionType.lettings, createType: CreateTransactionTypes.LEASE };
    }

    if (requirement.for_sale) {
      const createType = requirement.is_investment_sale
        ? CreateTransactionTypes.INVESTMENT_SALE
        : CreateTransactionTypes.OCCUPATIONAL_SALE;
      return { type: TransactionType.sales, createType };
    }

    return { type: TransactionType.lettings, createType: null };
  };

  const { type: drawerType, createType } = getTransactionConfig();

  if (pageState.publish_options === 'true') {
    const lettingsFieldList = getLettingsAtlasPublishFields();

    const previewTransaction: Partial<LettingTransactionsShowItem> = {
      building_types: [],
      id: 0,
      images: [],
      lease_end: null,
      lease_start: null,
      primary_address: '',
      rent_currency: '',
      rent_pa: null,
      rent_psf: null,
      secondary_address: '',
      signature_date: null,
      size_metric: spaceFormData.sizeMetric,
      size_value: Number(spaceFormData.sizeValue) || 0,
    };

    const handlePublishToAtlas = () => {
      // TODO: Implement actual publishing logic
    };

    return (
      <Drawer anchor="right" open onClose={handleClose}>
        <DrawerHeader header={t('publish_to_atlas')} onClose={handleClose} />
        <div className={classes.publishOptionsContent}>
          <AtlasPublishOptionsPanel
            transaction={previewTransaction as LettingTransactionsShowItem}
            fieldList={lettingsFieldList}
            fieldVisibility={fieldVisibility}
            onFieldVisibilityChange={setFieldVisibility}
          />
        </div>
        <CreateNewDrawerFooter
          text={t('back')}
          onBackClick={() => updatePageState({ publish_options: '' })}
          validationErrorText=""
          button={
            <NewButton
              colour={NewButtonColour.PrimaryNavy}
              text={t('save_transaction_and_publish_to_atlas')}
              onClick={handlePublishToAtlas}
            />
          }
        />
      </Drawer>
    );
  }

  const hasSelectedProperty =
    (spaceFormData.onShortlist === '0' && !!spaceFormData.matchIdSelected) ||
    (spaceFormData.onShortlist === '1' && spaceFormData.floorUnits.length > 0);

  if (requiresBothTenureOptions && tenureSelection === null) {
    return (
      <Drawer anchor="right" open onClose={handleClose}>
        <DrawerHeader header={t('complete_requirement_transaction')} onClose={handleClose} />
        {renderContent()}
      </Drawer>
    );
  }

  if (!hasSelectedProperty) {
    return (
      <Drawer anchor="right" open onClose={handleClose}>
        <DrawerHeader header={t('complete_requirement_transaction')} onClose={handleClose} />
        {renderContent()}
        {getFooterButtons()}
      </Drawer>
    );
  }

  return (
    <CreateTransactionDrawer open createType={createType} type={drawerType} onClose={handleClose} tagGroupOptions={[]}>
      <DrawerHeader header={t('complete_requirement_transaction')} onClose={handleClose} />
      {renderContent()}
      {getFooterButtons()}
    </CreateTransactionDrawer>
  );
};

export const RequirementTransactionDrawer = withStyles(styles)(RequirementTransactionDrawerComponent);
